// yup-field-builder.js
import * as Yup from 'yup';

/**
 * Build a Yup schema for a field based on type, required flag, label, and rules.
 * Supports default message fallback if rule.message is missing or empty.
 *
 * @param {Object} config
 * @param {'string'|'number'|'array'|'object'|'file'} config.type - The base field type
 * @param {boolean} config.required - Whether the field is required
 * @param {string} config.label - Field label used in error messages
 * @param {Object} config.rules - Validation rules (direct values or { value, message } format)
 * @returns {Yup.Schema} Yup validation schema
 *
 * Summary Rules
 * -------------
 * | Rule           | Field Types           | Description                                               |
 * |----------------|-----------------------|-----------------------------------------------------------|
 * | `min`, `max`   | string, number        | Min/max string length or number value                     |
 * | `exclude`      | string                | Regex to exclude characters (e.g. `/[^a-z]/g`)            |
 * | `mustInclude`  | string, array, object | Must contain substring, value in array, or key in object  |
 * | `email`        | string                | Must be a valid email format                              |
 * | `greaterThan`  | number                | Must be greater than the given number                     |
 * | `lessThan`     | number                | Must be less than the given number                        |
 * | `minLength`    | array                 | Array must contain at least N items                       |
 * | `unique`       | array                 | All values in array must be unique                        |
 * | `requiredKeys` | object                | Object must include specified keys                        |
 * | `mimeTypes`    | file, array.fileRules | File MIME type must be in whitelist (e.g. image/jpeg)     |
 * | `maxSize`      | file, array.fileRules | File must not exceed the specified size in bytes          |
 */

export const buildYupField = ({ type, required = false, label, rules = {} }) => {
  const getVal = (rule) => typeof rule === 'object' ? rule.value : rule;
  const getMsg = (rule, fallback) =>
    typeof rule === 'object' && rule.message ? rule.message : fallback;

  let schema;

  switch (type) {
    case 'string':
      schema = Yup.string().nullable();

      if (rules.min)
        schema = schema.min(getVal(rules.min), getMsg(rules.min, `${label} phải có ít nhất ${getVal(rules.min)} ký tự`));

      if (rules.max)
        schema = schema.max(getVal(rules.max), getMsg(rules.max, `${label} không được vượt quá ${getVal(rules.max)} ký tự`));

      if (rules.exclude)
        schema = schema.test(
          'exclude',
          getMsg(rules.exclude, `${label} chứa ký tự không hợp lệ`),
          (val) => !val || !rules.exclude.test(val)
        );

      if (rules.mustInclude)
        schema = schema.test(
          'must-include',
          getMsg(rules.mustInclude, `${label} phải chứa từ "${getVal(rules.mustInclude)}"`),
          (val) => !val || val.includes(getVal(rules.mustInclude))
        );

      if (rules.email)
        schema = schema.email(getMsg(rules.email, `${label} không đúng định dạng email`));

      break;

    case 'number':
      schema = Yup.number()
        .nullable()
        .transform((val, orig) => (orig === '' || orig === null ? null : Number(orig)))
        .typeError(`${label} không hợp lệ`);

      if (rules.greaterThan)
        schema = schema.moreThan(
          getVal(rules.greaterThan),
          getMsg(rules.greaterThan, `${label} phải lớn hơn ${getVal(rules.greaterThan)}`)
        );

      if (rules.lessThan)
        schema = schema.lessThan(
          getVal(rules.lessThan),
          getMsg(rules.lessThan, `${label} phải nhỏ hơn ${getVal(rules.lessThan)}`)
        );

      break;

    case 'array':
      schema = Yup.array().nullable();

      if (rules.minLength)
        schema = schema.test(
          'min-length',
          getMsg(rules.minLength, `${label} phải có ít nhất ${getVal(rules.minLength)} phần tử`),
          (val) => !val || val.length >= getVal(rules.minLength)
        );

      if (rules.mustInclude)
        schema = schema.test(
          'must-include',
          getMsg(rules.mustInclude, `${label} phải chứa giá trị "${getVal(rules.mustInclude)}"`),
          (val) => !val || val.includes(getVal(rules.mustInclude))
        );

      if (rules.unique)
        schema = schema.test(
          'unique',
          getMsg(rules.unique, `${label} không được chứa phần tử trùng lặp`),
          (val) => !val || new Set(val).size === val.length
        );

      // New: Validate each file inside array
      if (rules.fileRules) {
        const { mimeTypes, maxSize } = rules.fileRules;

        schema = schema.of(
          Yup.object().shape({
            file: Yup.mixed()
              .nullable()
              .test(
                'file-type',
                getMsg(mimeTypes, `${label} phải là ảnh ${getVal(mimeTypes)?.join(', ')}`),
                (val) =>
                  !val || !mimeTypes || getVal(mimeTypes).includes(val.type)
              )
              .test(
                'file-size',
                getMsg(maxSize, `${label} vượt quá kích thước cho phép`),
                (val) => !val || !maxSize || val.size <= getVal(maxSize)
              )
          })
        );
      }
      
      break;

    case 'object':
      schema = Yup.object().nullable();

      if (rules.requiredKeys?.length) {
        schema = schema.test(
          'required-keys',
          getMsg(rules.requiredKeys, `${label} không đầy đủ thông tin`),
          (val) =>
            !val ||
            rules.requiredKeys.every((key) => val?.[key] !== undefined && val[key] !== '')
        );
      } else {
        // Fallback: object must have at least 1 non-empty key
        schema = schema.test(
          'object-has-any',
          `${label} không được để trống`,
          (val) => !val || Object.values(val).some((v) => v !== undefined && v !== null && v !== '')
        );
      }

      if (rules.mustInclude) {
        schema = schema.test(
          'must-include-key',
          getMsg(rules.mustInclude, `${label} không chứa khóa "${getVal(rules.mustInclude)}"`),
          (val) => !val || Object.prototype.hasOwnProperty.call(val, getVal(rules.mustInclude))
        );
      }

      break;

    case 'file':
      schema = Yup.mixed().nullable();

      if (required) {
        if (rules.mimeTypes)
          schema = schema.test(
            'file-type',
            getMsg(rules.mimeTypes, `${label} phải là một trong các định dạng: ${getVal(rules.mimeTypes).join(', ')}`),
            (file) => !file || getVal(rules.mimeTypes).includes(file.type)
          );

        if (rules.maxSize)
          schema = schema.test(
            'file-size',
            getMsg(rules.maxSize, `${label} vượt quá kích thước cho phép`),
            (file) => !file || file.size <= getVal(rules.maxSize)
          );

        schema = schema.required(`${label} là bắt buộc`);
      } else {
        schema = schema.notRequired().nullable(); // skip test completely if not required
      }

      break;

    default:
      schema = Yup.mixed();
  }

  return required ? schema.required(`${label} là bắt buộc`) : schema.notRequired().nullable();
};

/**
 * applyRequiredRule
 * Lightweight conditional required wrapper for simple use cases.
 *
 * @param {Yup.Schema} schema - The base schema (e.g., Yup.string(), Yup.number())
 * @param {string} msg - Error message when required
 * @param {boolean} isRequired - Condition to enforce required
 * @returns {Yup.Schema}
 */
export const applyRequiredRule = (schema, msg, isRequired = true) => {
  return schema
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .when([], {
      is: () => isRequired,
      then: (s) => s.required(msg),
      otherwise: (s) => s.notRequired().nullable(),
    });
};


/** 
 * Sample Usage
 * ------------
import * as Yup from 'yup';
import { buildYupField, applyRequiredRule } from 'src/components/formik/yup-field-builder';

const schema = Yup.object({
  // Basic string with multiple rules
  username: buildYupField({
    type: 'string',
    required: true,
    label: 'Tên đăng nhập',
    rules: {
      min: 4,
      exclude: /[^a-zA-Z0-9]/g
    }
  }),

  // Email with default formatting
  email: buildYupField({
    type: 'string',
    required: true,
    label: 'Email',
    rules: { email: true }
  }),

  // Number with greaterThan and custom message
  age: buildYupField({
    type: 'number',
    required: true,
    label: 'Tuổi',
    rules: {
      greaterThan: { value: 17, message: 'Tuổi phải lớn hơn 17' }
    }
  }),

  // Array with inclusion and uniqueness
  skills: buildYupField({
    type: 'array',
    required: true,
    label: 'Kỹ năng',
    rules: {
      minLength: 2,
      mustInclude: 'JavaScript',
      unique: true
    }
  }),

  // Object with required keys
  profile: buildYupField({
    type: 'object',
    required: true,
    label: 'Hồ sơ',
    rules: {
      requiredKeys: ['id', 'name']
    }
  }),

  // Single image with required
  image: buildYupField({
    type: 'file',
    required: true,
    label: 'Ảnh đại diện',
    rules: {
      mimeTypes: { value: ['image/jpeg', 'image/png'] },
      maxSize: { value: 2 * 1024 * 1024, message: 'Ảnh phải nhỏ hơn 2MB' }
    }
  })

  // Multi images with required
  images: buildYupField({
    type: 'array',
    required: true,
    label: 'Hình ảnh',
    rules: {
      minLength: 1,
      fileRules: {
        mimeTypes: ['image/jpeg', 'image/png'],
        maxSize: {
          value: 2 * 1024 * 1024,
          message: 'Ảnh phải nhỏ hơn 2MB'
        }
      }
    }
  })

  // Fallback with applyRequiredRule
  note: applyRequiredRule(
    Yup.string().max(200, 'Ghi chú không quá 200 ký tự'),
    'Ghi chú là bắt buộc nếu chọn điều kiện',
    true // dynamic condition
  )
  
});
*/

/**
 * Sample Usage for applyRequiredRule
 * ------------
 export const validationPagers = (isRequired) => {
   return Yup.object({
     image: applyRequiredRule(Yup.string(), 'Hình giấy tờ yêu cầu phải có', isRequired),
     pagersField: applyRequiredRule(
       Yup.object().shape({
         value: Yup.string().required(),
         label: Yup.string().required(),
       }).nullable(),
       'Vui lòng chọn loại giấy tờ',
       isRequired
     ),
     expField: applyRequiredRule(
       Yup.number()
         .transform((val, orig) => (orig === '' || orig === null ? null : Number(orig)))
         .typeError('Ngày hết hạn không hợp lệ')
         .moreThan(0, 'Ngày hết hạn phải có')
         .nullable(), 
         'Ngày hết hạn phải có',
       isRequired
     ),
   });
 };
*/

/**
 * Sample Usage for Origin (handle manual)
 * ------------
 export const validationPagers = (isRequired) => {
   console.log('isRequired = ', isRequired);
   return Yup.object().shape({
     expField: Yup.string()
       .nullable()
       .when([], {
         is: () => isRequired,
         then: (schema) => schema.required('Ngày hết hạn phải có'),
         otherwise: (schema) => schema.nullable(),
       }),
     pagersField: Yup.object()
       .nullable()
       .when([], {
         is: () => isRequired,
         then: (schema) => schema.required('Vui lòng chọn loại giấy tờ'),
         otherwise: (schema) => schema.nullable(),
       }),
     image: Yup.string().when([], {
       is: () => isRequired,
       then: (schema) => schema.required('Hình giấy tờ yêu cầu phải có'),
       otherwise: (schema) => schema.nullable(),
     }),
   });
 };
 */